<template>
  <div id="provider-claim">
    <loader :loading="loading"/>
    <onboarding-template
      :darkLogo="true"
      :current-step="1"
      :total-steps="totalSteps"
      :show-progress="false"
      :is-next-button-enabled="enableNextStep && !loading"
      :show-next-button="currentStep !== 'createAccount'"
      :next-label="nextLabelText"
      @next-step="handleNextButton"
    >
      <h4 class="title font-heading-30">{{ stepTitle }}</h4>
      <p class="mt-1 error-message" v-if="!!errorMessages.length">
        {{ errorMessages }}
      </p>
      <simple-facility-card v-if="showFacilityCard"
        :key="localFacility.id" :facility-data="facility"
        :is-claimable="false" :claimed="true" :claiming="true"
        :claim-disabled="true" class="facility-card" />
      <onboarding-step v-if="['locationInfo', 'locationContact'].includes(currentStep)">
        <contact-summary v-if="isContactInfo"
          :phone-number="localFacility.phone_number"
          :email="localFacility.email"
        />
        <p class="sub-title" v-else>
          In order to claim your profile on Kinside, we need to verify you are the provider.
          It's free and only takes a minute.
        </p>
      </onboarding-step>
      <onboarding-step v-if="currentStep === 'createAccount'">
        <provider-register @submit="handleNextButton" :ccf-id="localFacility.id" />
      </onboarding-step>
      <onboarding-step v-if="currentStep === 'verifyMethod'">
        <verification-methods
          :phone-number="localFacility.phone_number"
          :email="localFacility.email"
          :verify-method="verifyMethod"
          @change="(val) => (verifyMethod = val)"/>
      </onboarding-step>
      <onboarding-step v-if="currentStep === 'verificationCode'">
        <verify-account
          @change="(val) => (codeEntered = val)"
          @resend-code="sendCode(userEmail, localFacility.phone_number, localFacility.email, true)"
          :code-entered="codeEntered"
          :email="verifyMethod === 'email' ? localFacility.email : undefined"
          :phoneNumber="localFacility.phone_number"
          :error="!!errorMessages.length"/>
      </onboarding-step>
      <onboarding-step v-if="['successMoreLocations', 'successViewProfile'].includes(currentStep)">
        <box class="m-b-48" color="viridian-light" faIcon="circle-check" :icnSolid="true">
          <p>You can now manage <span class="bold">{{ facilitiesName }}</span> on Kinside</p>
        </box>
        <div class="success-note" v-if="currentStep === 'successMoreLocations'">
          <p class="bold">Claim more locations!</p>
          <p>We found {{ moreFacilities?.length }} other locations
            in our system associated with your contact info.</p>
        </div>
        <div v-if="displayMoreFacilities">
          <simple-facility-card v-for="facility in moreFacilities"
            :key="facility.id"
            :facility-data="facility"
            :is-claimable="true"
            :claimed="facility.isClaimed"
            class="facility-card inner-margin"
            @claim-facility="claimFacility"
          />
        </div>
      </onboarding-step>
      <template v-slot:footer v-if="enableBtn">
        <system-button
         style-type="clear"
         :disabled="loading"
         @click="prevStep"
        >
          {{ prevLabelText }}
        </system-button>
      </template>
      <template v-if="currentStep === 'createAccount'">
        <div class="login-container">
          <p>Already have a Kinside account?</p>
          <a href="#" class="login-link" @click.prevent="switchToLogin">Log in</a>
        </div>
      </template>
    </onboarding-template>
    <provider-login v-if="currentStep === 'createAccount'">
      <GoogleOAuth />
    </provider-login>
  </div>
</template>

<script>
import { axios } from '@utils';
import AuthMixin from '@profile/mixins/auth';
import verifyRegistration from '@mixins/verifyRegistration';
import OnboardingTemplate from '@components/onboarding/onboarding-template';
import OnboardingStep from '@components/onboarding/onboarding-step';
import ContactSummary from '@components/onboarding/contact-summary.vue';
import ProviderRegister from '@care-provider/components/register.vue';
import ProviderLogin from '@components/login.vue';
import SimpleFacilityCard from '@components/simple-facility-card/simple-facility-card.vue';
import SystemButton from '@components/buttons/system-button.vue';
import Box from '@components/box.vue';
import Loader from '@components/loader/loader.vue';
import GoogleOAuth from '@components/google-o-auth.vue';

export default {
  name: 'profile-onboarding',
  components: {
    OnboardingTemplate,
    OnboardingStep,
    ContactSummary,
    ProviderRegister,
    ProviderLogin,
    GoogleOAuth,
    SimpleFacilityCard,
    SystemButton,
    Box,
    Loader,
  },
  mixins: [AuthMixin, verifyRegistration],
  props: {
    facility: {
      type: Object,
      default: () => { },
    },
  },
  data() {
    return {
      stepIndex: 0,
      userEmail: undefined,
      hasMoreLocations: false,
      showLocations: false,
      moreFacilities: [],
      localFacility: { ...this.facility },
      csrfToken: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
      loading: false,
    };
  },
  steps: [
    'locationInfo',
    'locationContact',
    'createAccount',
    'verifyMethod',
    'verificationCode',
    'successMoreLocations',
    'successViewProfile',
  ],
  mounted() {
    this.userData();
  },
  methods: {
    async userData() {
      this.$store.dispatch('init');
      await this.$store.dispatch('isInitialized');
      this.userEmail = this.$store.state.user.current.email;
    },
    prevStep() {
      if (this.stepIndex === 0) return;
      this.errorMessages = '';
      if (this.skippableStep) {
        this.moreFacilities.forEach((f) => { this.$set(f, 'isClaimed', false); });
        this.stepIndex += 1;
        return;
      }
      this.stepIndex -= 1;
    },
    async handleNextButton(data = {}) {
      if (this.stepIndex === this.totalSteps) return;
      if (this.errorMessages.length) this.errorMessages = '';
      if (this.isContactInfo && !!this.userEmail?.length) {
        if (this.userVerified()) {
          await this.checkMoreLocations();
          this.stepIndex += 3;
        } else this.stepIndex += 1;
      }
      if (this.currentStep === 'successViewProfile') {
        if (!this.claimedFacilityIds.includes(this.localFacility.id)) {
          this.claimedFacilities.push(this.localFacility);
        }
        const count = this.claimedFacilities.length;
        const params = {
          signup_from: 'claim_profile',
          user: {
            onboarding_completed: true,
            onboarding_version: 4,
            number_of_locations: count,
            claimed_facilities: this.claimedFacilities.map((f) => f.id),
          },
        };
        this.loading = true;
        await this.updateUser(params);
        this.loading = false;
        return;
      }
      if (this.currentStep === 'createAccount') {
        this.userEmail = data.email || this.userEmail;
      }
      if (this.currentStep === 'verifyMethod') {
        await this.sendCode(
          this.userEmail,
          this.localFacility.phone_number,
          this.localFacility.email,
        );
      }
      if (this.currentStep === 'verificationCode') {
        await this.verifyAccount(this.userEmail);
        if (!this.codeVerified) return;
        if (this.codeVerified) await this.checkMoreLocations();
        if (!this.hasMoreLocations) this.stepIndex += 1;
      }
      if (this.currentStep === 'successMoreLocations' && this.hasMoreLocations && !this.showLocations) {
        this.showLocations = true;
        return;
      }
      this.stepIndex += 1;
    },
    async checkMoreLocations() {
      await axios.get('/childcare/by_contact.json', {
        params:
        {
          email: (this.localFacility.email?.toLowerCase()),
          phone_number: this.localFacility.phone_number,
        },
      }).then((response) => {
        if (response.data.facilities?.length && response.status === 200) {
          this.moreFacilities = response.data.facilities?.filter(
            (f) => (this.localFacility.id !== f.id),
          );
          this.hasMoreLocations = !!this.moreFacilities?.length;
        }
      });
    },
    async updateUser(params) {
      await axios.put('/user.json', params)
        .then((response) => {
          if (response.status === 200) {
            window.location.href = '/';
          }
        });
    },
    claimFacility(facility) {
      const selectedFacility = this.moreFacilities.find((f) => f.id === facility.id);

      if (selectedFacility) {
        this.$set(selectedFacility, 'isClaimed', !selectedFacility?.isClaimed);
      }
    },
    userVerified() {
      return this.$store.state.user.current.verified;
    },
  },
  computed: {
    totalSteps() {
      return this.$options.steps.length;
    },
    currentStep() {
      return this.$options.steps[this.stepIndex];
    },
    showFacilityCard() {
      return !['verificationCode', 'successMoreLocations', 'successViewProfile'].includes(this.currentStep);
    },
    isContactInfo() {
      return this.currentStep === 'locationContact';
    },
    isVerifyMethod() {
      return this.currentStep === 'verifyMethod';
    },
    skippableStep() {
      return this.currentStep === 'successMoreLocations';
    },
    displayMoreFacilities() {
      return this.skippableStep && this.showLocations && this.moreFacilities?.length > 0;
    },
    claimedFacilities() {
      return this.moreFacilities.filter((f) => f.isClaimed);
    },
    claimedFacilityIds() {
      return this.claimedFacilities.map((f) => f.id);
    },
    facilitiesName() {
      const names = this.claimedFacilities.map((f) => f.name);
      names.push(this.localFacility.name);
      return names.join(', ');
    },
    enableNextStep() {
      if (this.currentStep === 'verifyMethod') {
        return !!this.verifyMethod?.length;
      }
      if (this.currentStep === 'verificationCode') {
        return this.codeEntered?.length === 5;
      }
      return true;
    },
    nextLabelText() {
      switch (this.currentStep) {
        case 'locationInfo':
          return 'Get verified';
        case 'verifyMethod':
          return 'Send';
        case 'verificationCode':
          return 'Go to my profile';
        case 'successMoreLocations':
          return this.showLocations ? 'Continue' : 'View locations';
        case 'successViewProfile':
          return 'View profile';
        default:
          return 'Continue';
      }
    },
    enableBtn() {
      const enableStep = !['locationInfo', 'verifyMethod'].includes(this.currentStep);
      const canClaimMore = this.currentStep === 'successViewProfile' ? !!this.moreFacilities?.length : true;
      return enableStep && canClaimMore;
    },
    prevLabelText() {
      switch (this.currentStep) {
        case 'successMoreLocations':
          return 'Skip for now';
        default:
          return 'Go back';
      }
    },
    stepTitle() {
      switch (this.currentStep) {
        case 'locationInfo':
        case 'locationContact':
          return 'Claim your business on Kinside';
        case 'createAccount':
          return 'Create your free account';
        case 'verifyMethod':
          return 'Choose a way to verify';
        case 'verificationCode':
          return 'Verification code sent';
        default:
          return 'Success!';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .page {
    position: unset;
  }
}

.error-message {
  color: var(--rust-40);
}

.bold {
  font-weight: 500;
}

.title {
  color: var(--viridian-50);
  text-align: center;
  margin-bottom: 1.5rem;
}

.sub-title {
  font-size: medium;
  text-align: center;
  font-weight: 400;
  padding: 1rem 0 2rem;
}

.login-container {
  text-align: center;

  .login-link {
    display: inline-block;
    margin-top: 5px;
    font-size: 16px;
  }
}

.success-note {
  padding: calc(var(--grid-unit) * 2);
  margin-bottom: 2rem;
  background-color: var(--periwinkle-0);
  color: var(--periwinkle-60);
  border-radius: var(--radius);
  border: 1px solid var(--periwinkle-15);
}

.inner-margin:not(:last-child) {
    margin-bottom: var(--grid-unit);
  }
</style>
